import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";

import {
  Building2,
  User,
  Mail,
  Globe,
  Phone,
  Target,
  BarChart3,
  TrendingUp,
  IndianRupee,
  Lightbulb,
  Rocket,
  Download,
  Calendar,
  Send,
  CheckCircle,
  WindArrowDown,
  Settings,
  Bot
} from "lucide-react";

import DonutScore from "./DonutScore";

import { useRef, useState } from "react";

const isMobile = window.innerWidth < 640;

export default function ReportDashboard({ report = {}, company = {} }) {
  const reportRef = useRef();

  const breakdown = report?.score_breakdown || {};

  const industryBenchmark = report?.industry_benchmark || {};

  const scoreData = [
    { name: "Data", value: breakdown.data_maturity || 0 },
    { name: "Automation", value: breakdown.automation || 0 },
    { name: "AI Readiness", value: breakdown.ai_readiness || 0 }
  ];

  const COLORS = ["#6366f1", "#22c55e", "#f59e0b"];
  const STORAGE_KEY = "ai-growth-scan";

  // ✅ PRINT PDF (PROFESSIONAL)
  const downloadPDF = () => {
    window.print();
  };

  const doneButton = () => {
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  };

  const timeline = [
    { phase: "Phase 1", duration: "0–3 Months", desc: "Foundation & Quick Wins" },
    { phase: "Phase 2", duration: "3–6 Months", desc: "Integration & Scaling" },
    { phase: "Phase 3", duration: "6–12 Months", desc: "Advanced AI Deployment" }
  ];

  const generateDynamicRoadmap = () => {
    const score = report?.score || 0;
    const challenges = (company?.challenges || "").toLowerCase();

    let focus = [];

    if (challenges.includes("manual") || challenges.includes("time")) {
      focus.push("Workflow Automation");
    }

    if (challenges.includes("sales") || challenges.includes("lead")) {
      focus.push("AI Lead Scoring");
    }

    if (challenges.includes("customer") || challenges.includes("support")) {
      focus.push("AI Chatbots");
    }

    if (score < 40) {
      return {
        level: "Early Stage",
        roadmap: [
          "Digitize business processes",
          "Centralize data systems",
          "Start with basic automation tools",
          ...focus
        ]
      };
    }

    if (score < 70) {
      return {
        level: "Growing",
        roadmap: [
          "Integrate AI into workflows",
          "Adopt predictive analytics",
          "Automate customer journeys",
          ...focus
        ]
      };
    }

    return {
      level: "Advanced",
      roadmap: [
        "Deploy custom AI models",
        "Optimize operations using AI",
        "Scale AI across departments",
        ...focus
      ]
    };
  };

  const getBenchmark = () => {
    const score = report?.score || 0;
    const industryAvg = industryBenchmark?.industry_avg_score || 65;
    const position = industryBenchmark?.position || "Lagging";

    let color = "#ef4444";

    if (position === "Leader") color = "#22c55e";
    else if (position === "Competitive") color = "#f59e0b";

    return {
      label: position,
      color,
      industryAvg
    };
  };

  const benchmark = getBenchmark();

  const getMaturityModel = () => {
    return report?.maturity_level || "Initial";
  };

  const InfoItem = ({ icon: Icon, label, value }) => (
    <div style={styles.infoItem}>
      <Icon size={16} />
      <div>
        <div style={styles.infoLabel}>{label}</div>
        <div style={styles.infoValue}>{value || "-"}</div>
      </div>
    </div>
  );

  const getProgressColor = (val) => {
    if (val < 30) return "#ef4444";
    if (val < 60) return "#f59e0b";
    return "#22c55e";
  };

  const phaseColors = [
    "linear-gradient(135deg,#eef2ff,#e0e7ff)", // soft indigo
    "linear-gradient(135deg,#ecfeff,#cffafe)", // soft cyan
    "linear-gradient(135deg,#f0fdf4,#dcfce7)"  // soft green
  ];

  const phaseIcons = [Settings, TrendingUp, Bot];
  const iconColors = ["#6366f1", "#06b6d4", "#22c55e"];

  const timelineData = [
    { phase: "Phase 1", duration: "0–3 Months", label: "Foundation & Quick Wins" },
    { phase: "Phase 2", duration: "3–6 Months", label: "Integration & Scaling" },
    { phase: "Phase 3", duration: "6–12 Months", label: "Advanced AI Deployment" }
  ];

  const scheduleMeeting = () => {
    const guests = encodeURIComponent(
      [company?.email, "contactus@emeistechnologies.com"]
        .filter(Boolean)
        .join(",")
    );

    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=AI Strategy Consultation&add=${guests}&location=Google+Meet`;

    window.open(url, "_blank");
  };

  const sendEmail = () => {
    const body = `
AI Growth Report

Company Name: ${company?.companyName}
Contact Person: ${company?.name}
Email: ${company?.email}
Mobile: ${company?.mobile}

Challenges: ${company?.challenges}

AI Score: ${report?.score}
ROI: ₹${report?.roi}
`;

    window.location.href = `mailto:contactus@emeistechnologies.com?subject=AI Report&body=${encodeURIComponent(body)}`;
  };

  const dynamicRoadmap = generateDynamicRoadmap();

  return (
    <div style={styles.wrapper}>
      <div style={styles.container} ref={reportRef}>
        <div style={styles.printHeader}>

          <div style={styles.headerLeft}>
            <img src="./emeis.png" style={styles.headerLogo} />
          </div>

          <div style={styles.headerRight}>
            <div style={styles.headerTitle}>AI Growth Report</div>

            <div style={styles.headerLinks}>
              <div
                style={styles.headerLink}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = "underline";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = "none";
                }}
              >
              <a href="mailto:contactus@emeistechnologies.com"
                target="_blank"
                rel="noreferrer"
                style={styles.headerLink}>
                <Mail size={14} />
                <span>
                  contactus@emeistechnologies.com
                </span>
                </a>
              </div>

              {/* DIVIDER (desktop only) */}
              {!isMobile && <div style={styles.divider} />}

              {/* WEBSITE */}
              <a
                href="https://www.emeistechnologies.com"
                target="_blank"
                rel="noreferrer"
                style={styles.headerLink}
              >
                <Globe size={14} />
                <span>www.emeistechnologies.com</span>
              </a>

            </div>
          </div>
        </div>

        {/* HERO */}
        <div style={styles.hero}>
          <div style={{ maxWidth: isMobile ? "100%" : "60%" }}>
            <h1 style={styles.heroTitle}>AI Growth Report</h1>
            <p style={styles.heroSubtitle}>
              Personalized AI Transformation Insights
            </p>
          </div>
          <div style={styles.heroStats}>
            <div style={styles.statCard}>
              <BarChart3 size={20} />
              <div style={styles.statValue}>{report?.score}</div>
              <div style={styles.statLabel}>AI Score</div>
            </div>

            <div style={styles.statCard}>
              <IndianRupee size={20} />
              <div style={styles.statValueGreen}>
                ₹{report?.roi ? report.roi.toLocaleString() : "0"}
              </div>
              <div style={styles.statLabel}>Estimated ROI</div>
            </div>
          </div>
        </div>

        {/* COMPANY */}
        <SectionCard title="Company Overview" icon={Building2}>
          <div style={styles.companyGrid}>
            <InfoItem icon={Building2} label="Company Name" value={company?.companyName} />
            <InfoItem icon={User} label="Contact Person" value={company?.name} />
            <InfoItem icon={Mail} label="Email" value={company?.email} />
            <InfoItem icon={Phone} label="Mobile" value={company?.mobile} />
            <InfoItem icon={Globe} label="Website" value={company?.website} />
          </div>
        </SectionCard>

        <SectionCard title="Current Challenges" icon={Target}>
          <div style={styles.grid}>
            <p>{company?.challenges || "-"}</p>
          </div>
        </SectionCard>

        {/* SCORE */}
        <SectionCard title="AI Maturity Score" icon={TrendingUp}>

          <div style={styles.donutWrapper}>
            <DonutScore
              value={report.score_breakdown.data_maturity}
              label="Data"
              color="#f59e0b"
            />

            <DonutScore
              value={report.score_breakdown.automation}
              label="Automation"
              color="#ef4444"
            />

            <DonutScore
              value={report.score_breakdown.ai_readiness}
              label="AI Ready"
              color="#22c55e"
            />
          </div>
        </SectionCard>

        <SectionCard title="AI Maturity Model" icon={TrendingUp}>
          <div style={styles.maturityBox}>
            <h2>{getMaturityModel()}</h2>
            <p>
              This reflects your organization's current AI capability and readiness level.
            </p>
          </div>
        </SectionCard>

        <SectionCard title="Industry Benchmarking" icon={BarChart3}>
          <div style={styles.benchmark}>
            <div>
              <h2 style={{ color: benchmark.color }}>
                {benchmark.label}
              </h2>
              <p>Your AI Score vs Industry Average</p>
            </div>

            <div style={styles.benchmarkBar}>
              <div
                style={{
                  ...styles.benchmarkFill,
                  width: `${report?.score}%`
                }}
              />
            </div>

            <p>
              Industry Avg: {benchmark.industryAvg} | Your Score: {report?.score}
            </p>
          </div>
        </SectionCard>

        {/* SCORE CHART */}
        <SectionCard title="AI Score Breakdown" icon={BarChart3}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={scoreData}>
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {scoreData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>

        <SectionCard title="Business Impact Potential" icon={TrendingUp}>
          <div style={styles.kpiGrid}>
            <div style={styles.kpiCard}>
              <h3>{report?.cost_reduction_percent || 0}%</h3>
              <p>Cost Reduction</p>
            </div>

            <div style={styles.kpiCard}>
              <h3>{report?.revenue_uplift_percent || 0}%</h3>
              <p>Revenue Uplift</p>
            </div>

            <div style={styles.kpiCard}>
              <h3>{report?.roi?.toLocaleString()}</h3>
              <p>Estimated ROI (₹)</p>
            </div>
          </div>
        </SectionCard>


        {/* OPPORTUNITIES */}
        <SectionCard title="AI Opportunities" icon={Lightbulb}>
          <ul>
            {(report?.opportunities || []).map((o, i) => (
              <li key={i} style={styles.opportunityItem}>
                <div style={styles.opportunityTitle}>
                  {o.title}
                </div>
                <div style={styles.opportunityDesc}>
                  {o.description}
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard title="Executive Summary" icon={Lightbulb}>
          <p style={styles.summary}>
            {report?.summary || "No summary available"}
          </p>
        </SectionCard>

        <SectionCard title="AI Transformation Roadmap" icon={Target}>
          <p style={styles.badge}>
            Maturity Level: <b>{dynamicRoadmap.level}</b>
          </p>

          <ul>
            {dynamicRoadmap.roadmap.map((item, i) => (
              <li key={i}>🧠 {item}</li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard title="AI Recommendations Roadmap" icon={Target}>
          <div style={styles.roadmapWrapper}>
            {report?.roadmap?.map((phase, i) => {
              const impact = Math.min(90, 60 + i * 10); // dynamic scoring

              return (
                <div key={i} style={styles.phaseItem}>

                  {/* CONNECTOR LINE */}
                  {i !== report.roadmap.length - 1 && (
                    <div style={styles.connector} />
                  )}

                  <div style={{
                    ...styles.phaseCard,
                    background: phaseColors[i % phaseColors.length]
                  }}>

                    {/* HEADER */}
                    <div style={styles.phaseHeader}>
                      <div style={styles.phaseIcon}>
                        {(() => {
                          const Icon = phaseIcons[i];
                          return <Icon size={22} color={iconColors[i]} />;
                        })()}
                      </div>

                      <div>
                        <div style={styles.phaseTitle}>
                          Phase {i + 1}
                        </div>
                        <div style={styles.phaseImpact}>
                          Impact: {impact}%
                        </div>
                      </div>
                    </div>

                    {/* CONTENT */}
                    <div style={styles.phaseContent}>
                      {phase.action}
                    </div>

                    {/* IMPACT BAR */}
                    <div style={styles.impactBar}>
                      <div
                        style={{
                          ...styles.impactFill,
                          width: `${impact}%`
                        }}
                      />
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>

        <SectionCard title="Implementation Timeline" icon={Calendar}>
          <div style={styles.timelineWrapper}>
            {timelineData.map((phase, i) => (
              <div key={i} style={styles.timelineItem}>

                {/* DOT */}
                <div style={styles.timelineDot}>
                  {(() => {
                    const Icon = phaseIcons[i];
                    return <Icon size={22} color={iconColors[i]} />;
                  })()}
                </div>

                {/* LINE */}
                {i !== timelineData.length - 1 && (
                  <div style={styles.timelineLine} />
                )}

                {/* CONTENT */}
                <div style={styles.timelineContent}>
                  <div style={styles.timelineTitle}>
                    {phase.phase} ({phase.duration})
                  </div>

                  <div style={styles.timelineDesc}>
                    {phase.label}
                  </div>

                  {/* IMPACT */}
                  <div style={styles.timelineImpact}>
                    Impact: {60 + i * 15}%
                  </div>
                </div>

              </div>
            ))}
          </div>
        </SectionCard>

        {/* CTA */}
        <div style={styles.ctaCard}>

          <div style={styles.ctaHeader}>
            <Rocket size={20} style={styles.ctaIcon} />
            <h2 style={styles.ctaTitle}>
              Start your AI Growth Journey with Emeis Technologies!
            </h2>
          </div>

          <p style={styles.ctaDesc}>
            Talk to our AI experts and unlock growth opportunities
          </p>

          <div style={styles.ctaLinks}>
            <a href="mailto:contactus@emeistechnologies.com" style={styles.ctaLink}>
              <Mail size={14} />
              <span>contactus@emeistechnologies.com</span>
            </a>

            <a
              href="https://www.emeistechnologies.com"
              target="_blank"
              rel="noreferrer"
              style={styles.ctaLink}
            >
              <Globe size={14} />
              <span>www.emeistechnologies.com</span>
            </a>
          </div>
        </div>
      </div>

      {/* ACTION BAR */}
      <div style={styles.actionBar} className="no-print">
        <button style={styles.primaryBtn} onClick={downloadPDF}>
          <Download size={16} /> Download PDF
        </button>

        <button style={styles.meetingBtn} onClick={scheduleMeeting}>
          <Calendar size={16} /> Book Call
        </button>

        <button style={styles.ghostBtn} onClick={sendEmail}>
          <Send size={16} /> Send Email
        </button>

        <button style={styles.ghostBtn} onClick={doneButton}>
          <CheckCircle size={16} /> Done
        </button>
      </div>

      <div style={styles.printFooter}>
        © Emeis Technologies — Confidential Report
      </div>

    </div>
  );
}

// ✅ REUSABLE CARD
const SectionCard = ({ title, icon: Icon, children }) => (
  <div style={styles.card}>

    <div style={styles.sectionHeader}>
      {Icon && (
        <div style={styles.sectionIcon}>
          <Icon size={16} />
        </div>
      )}

      <div style={styles.sectionTitleBadge}>
        {title}
      </div>
    </div>

    <div style={{ marginTop: 14 }}>
      {children}
    </div>

  </div>
);

// 🎨 PREMIUM STYLES
const styles = {
  wrapper: {
    background: "#f1f5f9",
    minHeight: "100vh",
    padding: "20px 12px",
    fontFamily: "Inter, sans-serif"
  },

  container: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "0 10px"
  },

  headerLeft: {
    display: "flex",
    alignItems: "center"
  },

  headerLogo: {
    width: isMobile ? 80 : 100
  },

  headerRight: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    alignItems: isMobile ? "flex-start" : "flex-end",
    width: "100%"
  },

  headerTitle: {
    fontWeight: 700,
    fontSize: isMobile ? 16 : 18
  },

  headerLinks: {
    display: "flex",
    flexDirection: isMobile ? "column" : "row", // 🔥 key
    gap: isMobile ? 6 : 16,
    flexWrap: "wrap"
  },

  headerLink: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: "#2563eb",
    textDecoration: "none",
    wordBreak: "break-all"
  },

  badge: {
    background: "#eef2ff",
    padding: "8px 12px",
    borderRadius: 8,
    display: "inline-block",
    marginBottom: 10
  },

  actionBar: {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
    marginTop: 30,
  },

  primaryBtn: {
    width: isMobile ? "100%" : "220px", // ❌ remove full stretch
    padding: "12px 18px",
    borderRadius: 12,
    border: "none",
    background: "linear-gradient(90deg,#6366f1,#8b5cf6)",
    color: "#fff",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8
  },

  meetingBtn: {
    width: isMobile ? "100%" : "200px",
    padding: "12px 18px",
    borderRadius: 12,
    border: "none",
    background: "linear-gradient(90deg,#f97316,#fb923c)",
    color: "#fff",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8
  },

  ghostBtn: {
    width: isMobile ? "100%" : "180px",
    padding: "12px 18px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8
  },

  benchmark: {
    textAlign: "center"
  },

  benchmarkBar: {
    height: 12,
    background: "#e5e7eb",
    borderRadius: 999,
    margin: "10px 0"
  },

  benchmarkFill: {
    height: "100%",
    background: "linear-gradient(90deg,#6366f1,#22c55e)",
    borderRadius: 999
  },

  maturityBox: {
    textAlign: "center",
    padding: 20,
    background: "#f8fafc",
    borderRadius: 12
  },

  icon: {
    marginRight: 8,
    verticalAlign: "middle"
  },

  linkIcon: {
    marginRight: 6,
    verticalAlign: "middle",
    flexShrink: 0
  },

  links: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginTop: 10
  },

  summary: {
    background: "#f0faac",
    borderRadius: 12,
    padding: 10,
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1.6,
    color: "#242424",
    textAlign: "justify"
  },

  roadmap: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 16
  },

  roadmapCard: {
    background: "linear-gradient(135deg,#ffffff,#f8fafc)",
    padding: 18,
    borderRadius: 16,
    border: "1px solid #e5e7eb",
    boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
    transition: "0.2s",
  },

  timeline: {
    display: "flex",
    flexDirection: "column",
    gap: 16
  },

  kpiCard: {
    background: "#f5f772",
    padding: 20,
    borderRadius: 14,
    textAlign: "center",
    border: "1px solid #e5e7eb",
    fontSize: 20,
    color: "#185f03",
  },

  companyGrid: {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 12
  },

  printHeader: {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    alignItems: isMobile ? "flex-start" : "center",
    gap: isMobile ? 10 : 0,
    marginBottom: 20,
    borderBottom: "2px solid #e5e7eb",
    paddingBottom: 10
  },

  printFooter: {
    marginTop: 40,
    fontSize: 12,
    textAlign: "center",
    color: "#64748b"
  },

  hero: {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    alignItems: isMobile ? "flex-start" : "center",
    justifyContent: "space-between",
    marginBottom: 24,
    gap: 16
  },

  heroTitle: {
    fontSize: isMobile ? 20 : 30,
    fontWeight: 800,
    color: "darkorange",
    lineHeight: 1.2,
    wordBreak: "break-word"
  },

  heroSubtitle: {
    color: "#64748b",
    fontSize: isMobile ? 14 : 16,
    marginTop: 6
  },

  heroStats: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    width: isMobile ? "100%" : "auto"
  },

  statCard: {
    flex: isMobile ? "1 1 45%" : "unset", // ✅ 2 per row mobile
    background: "#ffffff",
    padding: "14px",
    borderRadius: 12,
    textAlign: "center",
    border: "1px solid #e5e7eb",
    minWidth: 100
  },

  statValue: {
    fontSize: isMobile ? 24 : 32,
    fontWeight: 700,
    color: "#4f46e5"
  },

  statValueGreen: {
    fontSize: isMobile ? 24 : 32,
    fontWeight: 700,
    color: "#22c55e"
  },

  chartWrapper: {
    width: "100%",
    height: 240,
    marginTop: 10,
    pageBreakInside: "avoid",
    breakInside: "avoid"
  },

  statLabel: {
    fontSize: 12,
    color: "#64748b"
  },

  card: {
    background: "#ffffff",
    padding: isMobile ? 16 : "clamp(16px, 3vw, 28px)",
    borderRadius: 16,
    marginBottom: 20,
    border: "1px solid #e5e7eb",
    boxShadow: "0 8px 30px rgba(0,0,0,0.04)",
    transition: "all 0.2s ease"
  },

  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 14,
    color: "#4f46e5"
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: 12
  },

  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 20
  },

  kpiGrid: {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12
  },

  opportunityItem: {
    marginBottom: 16,
    padding: 14,
    borderRadius: 12,
    background: "#f8fafc",
    border: "1px solid #e5e7eb"
  },

  opportunityTitle: {
    fontWeight: 600,
    color: "#4975a1",
    marginBottom: 6
  },

  opportunityDesc: {
    fontSize: 13,
    color: "#50709e",
    lineHeight: 1.5
  },

  progressHeader: {
    display: "flex",
    justifyContent: "space-between"
  },

  progressTrack: {
    height: 12,
    background: "#e5e7eb",
    borderRadius: 999,
    overflow: "hidden"
  },

  progressFill: {
    height: "100%",
    borderRadius: 999,
    transition: "width 0.6s ease",
    boxShadow: "0 4px 10px rgba(0,0,0,0.1)"
  },

  cta: {
    textAlign: "center",
    padding: 40,
    background: "linear-gradient(135deg,#eef2ff,#f8fafc)",
    borderRadius: 24,
    marginTop: 30,
    border: "1px solid #e5e7eb"
  },

  infoItem: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    padding: 12,
    borderRadius: 12,
    background: "#f9fafb",
    border: "1px solid #f1f5f9"
  },

  infoLabel: {
    fontSize: 12,
    color: "#64748b"
  },

  infoValue: {
    fontWeight: 600
  },

  roadmapGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 20
  },

  phaseBadge: {
    display: "inline-block",
    background: "#111827",
    color: "#fff",
    fontSize: 12,
    padding: "6px 10px",
    borderRadius: 999,
    fontWeight: 600
  },

  roadmapWrapper: {
    display: "flex",
    gap: 16,
    position: "relative",
    flexWrap: "wrap",
    flexDirection: isMobile ? "column" : "row",
  },

  phaseItem: {
    position: "relative",
    flex: 1,
    minWidth: "100%"
  },

  connector: {
    position: "absolute",
    top: 40,
    right: -10,
    width: 20,
    height: 2,
    background: "#c7d2fe",
    zIndex: 1,
    display: isMobile ? "none" : "block"
  },

  phaseCard: {
    padding: 20,
    borderRadius: 16,
    border: "1px solid #e5e7eb",
    boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    minHeight: 220
  },

  phaseHeader: {
    display: "flex",
    gap: 12,
    alignItems: "center"
  },

  phaseIcon: {
    width: 42,
    height: 42,
    borderRadius: 12,
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
    color: "#f9e5c4" // brand color
  },

  phaseTitle: {
    fontWeight: 700
  },

  phaseImpact: {
    fontSize: 12,
    color: "#64748b"
  },

  phaseContent: {
    fontSize: 14,
    lineHeight: 1.6,
    textAlign: "justify",
    color: "#374151"
  },

  impactBar: {
    height: 8,
    background: "#e5e7eb",
    borderRadius: 999,
    overflow: "hidden",
    marginTop: "auto"
  },

  impactFill: {
    height: "100%",
    background: "linear-gradient(90deg,#6366f1,#22c55e)",
    transition: "width 0.6s ease"
  },

  timelineWrapper: {
    position: "relative",
    marginTop: 20
  },

  timelineItem: {
    display: "flex",
    gap: 12,
    position: "relative",
    marginBottom: 20
  },

  timelineDot: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#c7d2fe,#f8fafc)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    boxShadow: "0 6px 14px rgba(249, 243, 182, 0.4)"
  },

  timelineLine: {
    position: "absolute",
    left: 18,
    top: 36,
    width: 2,
    height: "100%",
    background: "#c7d2fe"
  },

  timelineContent: {
    background: "#f8fafc",
    padding: 12,
    borderRadius: 10,
    flex: 1
  },

  timelineTitle: {
    fontWeight: 600,
    fontSize: isMobile ? 14 : 16
  },

  timelineDesc: {
    color: "#475569",
    marginTop: 4,
    fontSize: isMobile ? 13 : 14
  },

  timelineImpact: {
    marginTop: 6,
    fontSize: 12,
    color: "#22c55e",
    fontWeight: 600
  },

  donutWrapper: {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    alignItems: "center",
    gap: 16,
    marginTop: 20
  },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10
  },

  sectionIcon: {
    width: 32,
    height: 32,
    borderRadius: 10,
    background: "#eef2ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#4f46e5"
  },

  sectionTitleBadge: {
    background: "linear-gradient(90deg,#eef2ff,#f8fafc)",
    color: "#4338ca",
    fontWeight: 600,
    fontSize: 14,
    padding: "6px 12px",
    borderRadius: 999,
    border: "1px solid #e0e7ff"
  },

  ctaCard: {
    background: "linear-gradient(135deg,#fff7ed,#ffedd5)",
    padding: "clamp(16px,4vw,28px)",
    borderRadius: 18,
    border: "1px solid #fed7aa",
    boxShadow: "0 10px 30px rgba(249,115,22,0.15)",
    marginBottom: 20
  },

  ctaHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap"
  },

  ctaIcon: {
    color: "#f97316",
    flexShrink: 0
  },

  ctaTitle: {
    color: "#ea580c",
    fontSize: "clamp(18px,4vw,22px)",
    fontWeight: 700,
    margin: 0,
    lineHeight: 1.3
  },

  ctaDesc: {
    color: "#7c2d12",
    marginTop: 10,
    fontWeight: 500,
    fontSize: "clamp(13px,3vw,15px)"
  },

  ctaLinks: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginTop: 16
  },

  ctaLink: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "#1d4ed8",
    textDecoration: "none",
    wordBreak: "break-all",
    background: "#fff",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #e5e7eb"
  },

  headerLinks: {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    alignItems: isMobile ? "flex-start" : "center",
    gap: isMobile ? 6 : 12,
    flexWrap: "wrap"
  },

  headerLink: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: "#2563eb",
    cursor: "pointer",
    position: "relative",
    textDecoration: "none"
  },

  /* ✨ Hover underline animation */
  headerLinkHover: {
    position: "relative"
  },

  divider: {
    width: 1,
    height: 14,
    background: "#cbd5f5"
  },

  /* 📋 copied badge */
  copied: {
    marginLeft: 8,
    fontSize: 10,
    color: "#16a34a",
    fontWeight: 600
  },
};